// ─── Pages (Dashboard, Collection, CardDetail, Landing, Auth) ─────
import { useState, useMemo, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Icons } from "./Icons";
import { GameBadge, CardImage, fmt, fmtPct, fmtDate, fmtPrice } from "./Shared";
import { getLatestPrice, getPriceSource, hasRealPrice, fetchRealPriceHistory } from "../api/priceService";
import { getEbayPriceSummary } from "../api/ebayService";
import { refreshCardData } from "../api/cardSearch";
import { auth, googleProvider } from "../config/firebase";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signInWithPopup, updateProfile } from "firebase/auth";

// ─── Helper: resolve card from collection item ───────────────────
function resolveCardFromItem(item) {
  return item.card_data || null;
}

// ─── InfoTooltip: ❓ icon with hover description ────────────────
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: "auto", padding: "0 8px" }}>
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        style={{ width: 22, height: 22, borderRadius: "50%", background: "var(--bg-card)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "help", fontSize: 12, fontWeight: 700, color: "var(--text-muted)" }}
      >
        ?
      </div>
      {show && (
        <div style={{ position: "absolute", right: 0, top: 32, width: 280, padding: 14, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.6, zIndex: 50, boxShadow: "0 8px 30px rgba(0,0,0,0.4)" }}>
          {text}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════
export const Dashboard = ({ collection, onNavigate, currency = "USD", exchangeRates = {}, ebayPriceCache = {}, setEbayPriceCache, collectionVersion = 0 }) => {
  const fmtC = (n) => fmtPrice(n, currency, exchangeRates);
  const [pricesLoading, setPricesLoading] = useState(false);

  // Fetch eBay prices — single loop, parallel batches of 8
  useEffect(() => {
    if (collection.length === 0) { setPricesLoading(false); return; }
    const cardsWithoutPrice = collection
      .map((item) => resolveCardFromItem(item))
      .filter((card) => card && getLatestPrice(card) === 0)
      .filter((card) => ebayPriceCache[card.id] === undefined);

    if (cardsWithoutPrice.length === 0) { setPricesLoading(false); return; }

    setPricesLoading(true);
    let cancelled = false;
    (async () => {
      for (let i = 0; i < cardsWithoutPrice.length; i += 8) {
        if (cancelled) break;
        const group = cardsWithoutPrice.slice(i, i + 8);
        const results = await Promise.allSettled(
          group.map((card) =>
            getEbayPriceSummary(card)
              .then((data) => ({ id: card.id, avg: data?.avg || 0 }))
              .catch(() => ({ id: card.id, avg: 0 }))
          )
        );
        if (cancelled || !setEbayPriceCache) break;
        setEbayPriceCache((prev) => {
          const updated = { ...prev };
          results.forEach((r) => {
            if (r.status === "fulfilled") updated[r.value.id] = r.value.avg;
          });
          group.forEach((card) => { if (updated[card.id] === undefined) updated[card.id] = 0; });
          return updated;
        });
        if (i + 8 < cardsWithoutPrice.length) await new Promise((r) => setTimeout(r, 300));
      }
      if (!cancelled) setPricesLoading(false);
    })();
    return () => { cancelled = true; };
  }, [collection.length, collectionVersion]);

  const metrics = useMemo(() => {
    let totalInvested = 0, totalValue = 0;
    const items = collection.map((item) => {
      const card = resolveCardFromItem(item);
      let latestPrice = getLatestPrice(card);
      let priceSource = getPriceSource(card);
      if (latestPrice === 0 && card && ebayPriceCache[card.id]) {
        latestPrice = ebayPriceCache[card.id];
        priceSource = "eBay";
      }
      const cost = item.purchase_price * item.quantity;
      const value = latestPrice * item.quantity;
      totalInvested += cost; totalValue += value;
      return { ...item, card, latestPrice, priceSource, cost, value, gain: value - cost, gainPct: cost > 0 ? (value - cost) / cost : 0 };
    });
    const pnl = totalValue - totalInvested;
    const pnlPct = totalInvested > 0 ? pnl / totalInvested : 0;
    const sorted = [...items].sort((a, b) => b.gainPct - a.gainPct);
    return { totalInvested, totalValue, pnl, pnlPct, items, topGainers: sorted.filter((i) => i.gain > 0).slice(0, 5), topLosers: sorted.filter((i) => i.gain < 0).reverse().slice(0, 5), top5Expensive: [...items].sort((a, b) => b.value - a.value).slice(0, 5), cardCount: items.reduce((s, i) => s + i.quantity, 0), distinctCards: items.length };
  }, [collection, ebayPriceCache]);

  if (collection.length === 0) return (
    <div>
      <div className="page-header"><h1 className="page-title">Dashboard</h1><p className="page-subtitle">Your portfolio overview</p></div>
      <div className="empty-state"><div style={{ fontSize: 48, marginBottom: 16 }}>🃏</div><h3>Your collection is empty</h3><p>Add your first card to start tracking your portfolio value.</p><button className="btn btn-primary btn-lg" onClick={() => onNavigate("add")}><Icons.Plus /> Add Your First Card</button></div>
    </div>
  );

  const rankColors = ["gold", "silver", "bronze", "", ""];
  const roiColor = metrics.pnlPct >= 0 ? "var(--green)" : "var(--red)";
  const roiSoftColor = metrics.pnlPct >= 0 ? "var(--green-soft)" : "var(--red-soft)";

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Portfolio performance & market overview</p>
      </div>

      {/* Updating banner */}
      {pricesLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", marginBottom: 16, background: "var(--bg-elevated)", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", fontSize: 13, color: "var(--text-muted)" }}>
          <Icons.Spinner /> Updating market prices...
        </div>
      )}

      <div className="profit-grid">
        <div className="profit-card" style={{ animation: "fadeIn 0.3s ease" }}><div className="accent-bar" style={{ background: "var(--accent)" }} /><div className="profit-icon" style={{ background: "var(--accent-soft)", color: "var(--accent)" }}><Icons.Collection /></div><div className="profit-label">Total Invested</div><div className="profit-value">{fmtC(metrics.totalInvested)}</div><div className="profit-sub" style={{ color: "var(--text-secondary)" }}>{metrics.cardCount} cards · {metrics.distinctCards} unique</div></div>
        <div className="profit-card" style={{ animation: "fadeIn 0.35s ease" }}><div className="accent-bar" style={{ background: "var(--accent)" }} /><div className="profit-icon" style={{ background: "var(--accent-soft)", color: "var(--accent)" }}><Icons.TrendUp /></div><div className="profit-label">Market Value</div><div className="profit-value">{fmtC(metrics.totalValue)}</div><div className="profit-sub" style={{ color: "var(--text-secondary)" }}>Based on market prices</div></div>
        <div className="profit-card" style={{ animation: "fadeIn 0.4s ease" }}><div className="accent-bar" style={{ background: metrics.pnl >= 0 ? "var(--green)" : "var(--red)" }} /><div className="profit-icon" style={{ background: metrics.pnl >= 0 ? "var(--green-soft)" : "var(--red-soft)", color: metrics.pnl >= 0 ? "var(--green)" : "var(--red)" }}>{metrics.pnl >= 0 ? <Icons.TrendUp /> : <Icons.TrendDown />}</div><div className="profit-label">Unrealized P&L</div><div className="profit-value" style={{ color: metrics.pnl >= 0 ? "var(--green)" : "var(--red)" }}>{metrics.pnl >= 0 ? "+" : ""}{fmtC(metrics.pnl)}</div><div className="profit-sub" style={{ color: metrics.pnl >= 0 ? "var(--green)" : "var(--red)" }}>{fmtPct(metrics.pnlPct)} all time</div></div>
        <div className="profit-card" style={{ animation: "fadeIn 0.45s ease" }}><div className="accent-bar" style={{ background: roiColor }} /><div className="profit-icon" style={{ background: roiSoftColor, color: roiColor }}><Icons.Percent /></div><div className="profit-label">ROI</div><div className="profit-value" style={{ color: roiColor }}>{fmtPct(metrics.pnlPct)}</div><div className="profit-sub" style={{ color: "var(--text-secondary)" }}>Return on investment</div></div>
      </div>
      <div className="dash-section">
        <div className="dash-section-title"><Icons.TrendUp /> Portfolio Summary</div>
        <div className="summary-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
          <div className="card" style={{ padding: 12, minWidth: 0, overflow: "hidden" }}><div className="card-label">Avg Card Value</div><div className="card-value" style={{ marginTop: 4, fontSize: "clamp(13px, 3.5vw, 20px)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fmtC(metrics.distinctCards > 0 ? metrics.totalValue / metrics.distinctCards : 0)}</div></div>
          <div className="card" style={{ padding: 12, minWidth: 0, overflow: "hidden" }}><div className="card-label">Cards with Prices</div><div className="card-value" style={{ marginTop: 4, color: "var(--green)", fontSize: "clamp(13px, 3.5vw, 20px)" }}>{metrics.items.filter((i) => i.latestPrice > 0).length} / {metrics.distinctCards}</div></div>
          <div className="card" style={{ padding: 12, minWidth: 0, overflow: "hidden" }}><div className="card-label">Total Cards</div><div className="card-value" style={{ marginTop: 4, fontSize: "clamp(13px, 3.5vw, 20px)" }}>{metrics.cardCount}</div><div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{metrics.distinctCards} unique</div></div>
        </div>
      </div>
      <div className="dash-row">
        <div className="dash-section">
          <div className="dash-section-title"><Icons.Star style={{ color: "#FFD700" }} /> Top 5 Most Valuable Cards</div>
          <div className="top5-list">
            {metrics.top5Expensive.map((item, i) => (
              <div key={item.id} className="top5-item" onClick={() => onNavigate("detail", item.card_id)}>
                <span className={`top5-rank ${rankColors[i]}`}>#{i + 1}</span>
                {item.card?.image_url ? <img src={item.card.image_small || item.card.image_url} className="top5-thumb" alt="" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} /> : <div className="top5-thumb-ph">{item.card?.game === "pokemon" ? "⚡" : "🏴‍☠️"}</div>}
                <div className="top5-info"><div className="top5-name">{item.card?.name}</div><div className="top5-meta">{item.card?.set_code} · {item.card?.rarity}</div></div>
                <div className="top5-value"><div className="top5-price">{fmtC(item.latestPrice)}</div><div className="top5-total">{item.quantity}× = {fmtC(item.value)}</div></div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="dash-section">
            <div className="dash-section-title" style={{ color: "var(--green)" }}><Icons.TrendUp /> Top Gainers</div>
            {metrics.topGainers.length === 0 ? <div style={{ color: "var(--text-muted)", fontSize: 13 }}>No gainers yet</div> : metrics.topGainers.map((item, i) => (
              <div key={item.id} className="mover-item" onClick={() => onNavigate("detail", item.card_id)} style={{ cursor: "pointer", marginBottom: 6 }}><span className="mover-rank">#{i + 1}</span><span className="mover-name">{item.card?.name}</span><span className="mover-change gain-positive">+{fmtPct(item.gainPct)}</span></div>
            ))}
          </div>
          <div className="dash-section">
            <div className="dash-section-title" style={{ color: "var(--red)" }}><Icons.TrendDown /> Top Losers</div>
            {metrics.topLosers.length === 0 ? <div style={{ color: "var(--text-muted)", fontSize: 13 }}>No losers yet</div> : metrics.topLosers.map((item, i) => (
              <div key={item.id} className="mover-item" onClick={() => onNavigate("detail", item.card_id)} style={{ cursor: "pointer", marginBottom: 6 }}><span className="mover-rank">#{i + 1}</span><span className="mover-name">{item.card?.name}</span><span className="mover-change gain-negative">{fmtPct(item.gainPct)}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// COLLECTION PAGE
// ═══════════════════════════════════════════════════════════════════
export const CollectionPage = ({ collection, onNavigate, onDelete, currency = "USD", exchangeRates = {}, ebayPriceCache = {} }) => {
  const fmtC = (n) => fmtPrice(n, currency, exchangeRates);
  const [search, setSearch] = useState("");
  const [gameFilter, setGameFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  const items = useMemo(() => {
    let list = collection.map((item) => {
      const card = resolveCardFromItem(item);
      let latestPrice = getLatestPrice(card);
      let priceSource = getPriceSource(card);
      if (latestPrice === 0 && card && ebayPriceCache[card.id]) {
        latestPrice = ebayPriceCache[card.id];
        priceSource = "eBay";
      }
      return { ...item, card, latestPrice, priceSource, cost: item.purchase_price * item.quantity, value: latestPrice * item.quantity, gain: (latestPrice - item.purchase_price) * item.quantity, gainPct: item.purchase_price > 0 ? (latestPrice - item.purchase_price) / item.purchase_price : 0 };
    });
    if (search) list = list.filter((i) => i.card?.name?.toLowerCase().includes(search.toLowerCase()));
    if (gameFilter !== "all") list = list.filter((i) => i.card?.game === gameFilter);
    if (sortBy === "name") list.sort((a, b) => (a.card?.name || "").localeCompare(b.card?.name || ""));
    else if (sortBy === "value") list.sort((a, b) => b.value - a.value);
    else if (sortBy === "gain") list.sort((a, b) => b.gain - a.gain);
    else if (sortBy === "date") list.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
    return list;
  }, [collection, search, gameFilter, sortBy, ebayPriceCache]);

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div><h1 className="page-title">Collection</h1><p className="page-subtitle">{collection.length} cards in your portfolio</p></div>
        <button className="btn btn-primary" onClick={() => onNavigate("add")}><Icons.Plus /> Add Card</button>
      </div>
      <div className="search-bar"><Icons.Search /><input className="input" placeholder="Search your collection..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
      <div className="filter-bar">
        {["all", "pokemon", "one_piece"].map((g) => (<button key={g} className={`filter-chip ${gameFilter === g ? "active" : ""}`} onClick={() => setGameFilter(g)}>{g === "all" ? "All" : g === "pokemon" ? "Pokémon" : "One Piece"}</button>))}
        <div style={{ flex: 1 }} />
        <select className="input" style={{ width: 140, padding: "6px 10px" }} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="name">Sort: Name</option><option value="value">Sort: Value</option><option value="gain">Sort: Gain</option><option value="date">Sort: Recent</option>
        </select>
      </div>
      {items.length === 0 ? (
        <div className="empty-state"><h3>No cards found</h3><p>{collection.length === 0 ? "Start building your collection." : "Try adjusting your filters."}</p>{collection.length === 0 && <button className="btn btn-primary" onClick={() => onNavigate("add")}><Icons.Plus /> Add Card</button>}</div>
      ) : (
        <>
          <div className="col-header"><span></span><span>Card</span><span>Qty</span><span>Avg Cost</span><span>Mkt Price</span><span>Total Value</span><span>P&L</span><span></span></div>
          <div className="collection-grid">
            {items.map((item) => (
              <div key={item.id} className="collection-item" onClick={() => onNavigate("detail", item.card_id)}>
                {/* Desktop layout (grid columns) */}
                <div className="col-desktop"><CardImage card={item.card || {}} /></div>
                <div className="col-desktop"><div className="card-info-name">{item.card?.name}</div><div className="card-info-meta"><GameBadge game={item.card?.game} /> {item.card?.set_code} #{item.card?.card_number}</div></div>
                <div className="col-desktop mono">{item.quantity}</div>
                <div className="col-desktop mono">{fmtC(item.purchase_price)}</div>
                <div className="col-desktop mono">{fmtC(item.latestPrice)}</div>
                <div className="col-desktop mono">{fmtC(item.value)}</div>
                <div className="col-desktop"><span className={`mono ${item.gain >= 0 ? "gain-positive" : "gain-negative"}`}>{item.gain >= 0 ? "+" : ""}{fmtC(item.gain)}<br /><span style={{ fontSize: 11 }}>{fmtPct(item.gainPct)}</span></span></div>
                <div className="col-desktop" style={{ display: "flex", gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onNavigate("edit", item.id); }} title="Edit"><Icons.Edit /></button>
                  <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onDelete(item.id); }} title="Remove" style={{ color: "var(--red)" }}><Icons.Trash /></button>
                </div>

                {/* Mobile layout (structured card) */}
                <div className="col-mobile">
                  <div className="col-mobile-top">
                    <div className="col-mobile-img"><CardImage card={item.card || {}} /></div>
                    <div className="col-mobile-info">
                      <div className="card-info-name">{item.card?.name}</div>
                      <div className="card-info-meta">{item.card?.set_name}</div>
                      <div className="card-info-meta"><GameBadge game={item.card?.game} /> #{item.card?.card_number} · {item.quantity}×</div>
                    </div>
                  </div>
                  <div className="col-mobile-prices">
                    <div className="col-mobile-price-item"><span className="col-mobile-label">Cost</span><span className="mono">{fmtC(item.purchase_price)}</span></div>
                    <div className="col-mobile-price-item"><span className="col-mobile-label">Market</span><span className="mono">{fmtC(item.latestPrice)}</span></div>
                    <div className="col-mobile-price-item"><span className="col-mobile-label">Value</span><span className="mono" style={{ fontWeight: 700 }}>{fmtC(item.value)}</span></div>
                    <div className="col-mobile-price-item"><span className="col-mobile-label">P&L</span><span className={`mono ${item.gain >= 0 ? "gain-positive" : "gain-negative"}`} style={{ fontWeight: 700 }}>{item.gain >= 0 ? "+" : ""}{fmtC(item.gain)} ({fmtPct(item.gainPct)})</span></div>
                  </div>
                  <div className="col-mobile-actions">
                    <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onNavigate("edit", item.id); }}><Icons.Edit /> Edit</button>
                    <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onDelete(item.id); }} style={{ color: "var(--red)" }}><Icons.Trash /> Remove</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// CARD DETAIL PAGE (with price tabs)
// ═══════════════════════════════════════════════════════════════════
export const CardDetailPage = ({ cardId, collection, onNavigate, currency = "USD", exchangeRates = {}, ebayPriceCache = {}, setEbayPriceCache }) => {
  const fmtC = (n) => fmtPrice(n, currency, exchangeRates);
  const [priceTab, setPriceTab] = useState("tcgplayer");
  const [priceHistory, setPriceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [ebayData, setEbayData] = useState(null);
  const [ebayLoading, setEbayLoading] = useState(false);
  const [liveCard, setLiveCard] = useState(null);
  const userItems = collection.filter((ci) => ci.card_id === cardId);
  const savedCard = userItems.length > 0 ? resolveCardFromItem(userItems[0]) : null;
  const card = liveCard || savedCard;

  // Price with eBay fallback
  const latestPrice = getLatestPrice(card, ebayData);
  const priceSource = getPriceSource(card, ebayData);

  // Refresh card prices from API on mount
  useEffect(() => {
    if (!savedCard) return;
    refreshCardData(savedCard).then((fresh) => {
      if (fresh) setLiveCard(fresh);
    });
  }, [savedCard?.id]);

  // Fetch real price history
  useEffect(() => {
    if (!card) return;
    setHistoryLoading(true);
    fetchRealPriceHistory(card).then((data) => {
      setPriceHistory(data);
      setHistoryLoading(false);
    });
  }, [card?.id]);

  // Always fetch eBay data on mount (used as fallback price + for eBay tab)
  useEffect(() => {
    if (!card || ebayData) return;
    let cancelled = false;
    setEbayLoading(true);
    getEbayPriceSummary(card)
      .then((data) => {
        if (!cancelled) {
          setEbayData(data || { avg: null, low: null, high: null, count: 0, listings: [] });
          setEbayLoading(false);
          // Save to shared cache
          if (data?.avg && setEbayPriceCache) {
            setEbayPriceCache((prev) => ({ ...prev, [card.id]: data.avg }));
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setEbayData({ avg: null, low: null, high: null, count: 0, listings: [], error: err.message });
          setEbayLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [card?.id]);

  if (!card) return <div><button className="btn btn-ghost" onClick={() => onNavigate("collection")}><Icons.Back /> Back</button><p style={{ marginTop: 20 }}>Card not found.</p></div>;

  const totalQty = userItems.reduce((s, i) => s + i.quantity, 0);
  const totalCost = userItems.reduce((s, i) => s + i.purchase_price * i.quantity, 0);
  const totalValue = latestPrice * totalQty;
  const pnl = totalValue - totalCost;
  const avgCost = totalQty > 0 ? totalCost / totalQty : 0;

  // Extract TCGPlayer price details
  const tcgPrices = card.tcgplayer_prices || {};
  const tcgPriceTypes = Object.keys(tcgPrices).filter((k) => tcgPrices[k]);

  // Extract Cardmarket price details
  const cmPrices = card.cardmarket_prices || {};

  // One Piece specific prices
  const opMarket = card.market_price;
  const opInventory = card.inventory_price;

  const PriceRow = ({ label, value, color }) => (
    <div className="price-detail-row">
      <span className="price-detail-label">{label}</span>
      <span className="price-detail-value" style={{ color: color || "var(--text-primary)" }}>{value != null ? fmtC(value) : "—"}</span>
    </div>
  );

  return (
    <div>
      <button className="btn btn-ghost" onClick={() => onNavigate("collection")} style={{ marginBottom: 16 }}><Icons.Back /> Back to Collection</button>
      <div className="detail-header">
        <div className="detail-image">
          {card.image_url ? <img src={card.image_url} alt={card.name} style={{ width: "100%" }} loading="lazy" /> : <div className="detail-image-placeholder"><span style={{ fontSize: 48 }}>{card.game === "pokemon" ? "⚡" : "🏴‍☠️"}</span><span style={{ color: "var(--text-muted)" }}>{card.card_number}</span></div>}
        </div>
        <div className="detail-info">
          <div className="detail-meta"><GameBadge game={card.game} /><span className="detail-badge">{card.rarity}</span>{card.variant && card.variant !== "Standard" && card.variant !== "Pokémon" && <span className="detail-badge">{card.variant}</span>}</div>
          <h1 className="detail-title" style={{ marginTop: 10 }}>{card.name}</h1>
          <div style={{ color: "var(--text-secondary)", fontSize: 14, marginTop: 4 }}>{card.set_name} · {card.set_code} · #{card.card_number}</div>
          {card.card_color && <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 4 }}>Color: {card.card_color} {card.card_type ? `· ${card.card_type}` : ""} {card.card_power ? `· Power: ${card.card_power}` : ""}</div>}
          <div className="detail-stats">
            <div className="card"><div className="card-label">Market Price</div><div className="card-value">{latestPrice > 0 ? fmtC(latestPrice) : (ebayLoading ? "..." : "—")}</div><div className="card-sub" style={{ color: priceSource === "eBay" ? "var(--amber)" : "var(--text-muted)" }}>{priceSource === "N/A" ? (ebayLoading ? "Loading eBay price..." : "No price data") : `via ${priceSource}`}</div></div>
            {totalQty > 0 && (<>
              <div className="card"><div className="card-label">Your Holdings</div><div className="card-value">{totalQty}×</div><div className="card-sub" style={{ color: "var(--text-secondary)" }}>Avg cost: {fmtC(avgCost)}</div></div>
              <div className="card"><div className="card-label">Unrealized P&L</div><div className="card-value" style={{ color: pnl >= 0 ? "var(--green)" : "var(--red)" }}>{pnl >= 0 ? "+" : ""}{fmtC(pnl)}</div><div className="card-sub" style={{ color: pnl >= 0 ? "var(--green)" : "var(--red)" }}>{totalCost > 0 ? fmtPct((totalValue - totalCost) / totalCost) : "N/A"}</div></div>
            </>)}
          </div>
        </div>
      </div>

      {/* ── Price Tabs ── */}
      <div className="card" style={{ marginBottom: 28, padding: 0, overflow: "hidden", maxWidth: "100%", boxSizing: "border-box" }}>
        <div className="price-tabs" style={{ overflow: "hidden" }}>
          <button className={`price-tab ${priceTab === "tcgplayer" ? "active" : ""}`} onClick={() => setPriceTab("tcgplayer")}><img src="/logos/tcgplayer.png" alt="TCGPlayer" style={{ height: 20 }} /></button>
          <button className={`price-tab ${priceTab === "cardmarket" ? "active" : ""}`} onClick={() => setPriceTab("cardmarket")}><img src="/logos/cardmarket.png" alt="Cardmarket" style={{ height: 16 }} /></button>
          <button className={`price-tab ${priceTab === "ebay" ? "active" : ""}`} onClick={() => setPriceTab("ebay")}><img src="/logos/ebay.png" alt="eBay" style={{ height: 18 }} /></button>
          <button className={`price-tab ${priceTab === "chart" ? "active" : ""}`} onClick={() => setPriceTab("chart")}>📈 Price Chart</button>
          <InfoTooltip text={
            priceTab === "tcgplayer" ? "Market prices from TCGPlayer — the largest US trading card marketplace. Shows market price (weighted average of recent sales), mid, low, and high values." :
            priceTab === "cardmarket" ? "Prices from Cardmarket — Europe's largest TCG marketplace. Includes average sell price, trend price, and 1/7/30 day moving averages." :
            priceTab === "ebay" ? "Active listing prices from eBay. Shows average, low, and high prices from current Buy It Now listings with links to view on eBay." :
            "Visual price trend over the last 90 days. Chart is anchored to the current market price from available sources."
          } />
        </div>

        <div style={{ padding: "16px 14px 20px", overflow: "hidden", wordBreak: "break-word" }}>

          {/* ── TCGPlayer Tab ── */}
          {priceTab === "tcgplayer" && (
            <div>
              {/* Pokémon: detailed TCGPlayer prices */}
              {card.game === "pokemon" && tcgPriceTypes.length > 0 ? (
                <>
                  {tcgPriceTypes.map((type) => (
                    <div key={type} style={{ marginBottom: 20 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--accent)", marginBottom: 8, textTransform: "capitalize" }}>{type.replace(/([A-Z])/g, " $1").trim()}</div>
                      <PriceRow label="Market Price" value={tcgPrices[type].market} color="var(--green)" />
                      <PriceRow label="Mid" value={tcgPrices[type].mid} />
                      <PriceRow label="Low" value={tcgPrices[type].low} />
                      <PriceRow label="High" value={tcgPrices[type].high} />
                      {tcgPrices[type].directLow && <PriceRow label="Direct Low" value={tcgPrices[type].directLow} />}
                    </div>
                  ))}
                </>
              ) : card.game === "one_piece" && opMarket ? (
                /* One Piece: OPTCG API prices */
                <>
                  {(card.variant === "Parallel Art" || (card.name || "").includes("(SP)") || (card.name || "").includes("(Parallel)")) ? (
                    <div className="price-coming-soon">
                      <div style={{ fontSize: 36, marginBottom: 12 }}>🔀</div>
                      <h4>SP / Parallel Variant</h4>
                      <p>TCGPlayer does not provide separate pricing for SP and Parallel variants. The eBay tab shows the real market price for this specific variant.</p>
                      <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={() => setPriceTab("ebay")}>View eBay Price →</button>
                    </div>
                  ) : (
                    <>
                      <PriceRow label="Market Price" value={opMarket} color="var(--green)" />
                      <PriceRow label="Inventory Price" value={opInventory} />
                      {card.date_scraped && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 12 }}>Last updated: {card.date_scraped}</div>}
                    </>
                  )}
                </>
              ) : (
                <div className="price-coming-soon">
                  <h4>No TCGPlayer data available</h4>
                  <p>Price data is not available for this card from TCGPlayer. The displayed price is an estimate based on card rarity.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Cardmarket Tab ── */}
          {priceTab === "cardmarket" && (
            <div>
              {cmPrices.averageSellPrice ? (
                <>
                  <PriceRow label="Average Sell Price" value={cmPrices.averageSellPrice} color="var(--green)" />
                  <PriceRow label="Trend Price" value={cmPrices.trendPrice} />
                  <PriceRow label="Low Price" value={cmPrices.lowPrice} />
                  {cmPrices.avg1 && <PriceRow label="Average (1 day)" value={cmPrices.avg1} />}
                  {cmPrices.avg7 && <PriceRow label="Average (7 days)" value={cmPrices.avg7} />}
                  {cmPrices.avg30 && <PriceRow label="Average (30 days)" value={cmPrices.avg30} />}
                </>
              ) : (
                <div className="price-coming-soon">
                  <h4>No Cardmarket data available</h4>
                  <p>{card.game === "one_piece" ? "Cardmarket prices are not yet available for One Piece cards in this version." : "This card does not have Cardmarket pricing data."}</p>
                </div>
              )}
            </div>
          )}

          {/* ── eBay Tab ── */}
          {priceTab === "ebay" && (
            <div>
              {ebayLoading && <div style={{ textAlign: "center", padding: 40, color: "var(--text-secondary)" }}><Icons.Spinner /><p style={{ marginTop: 12 }}>Searching eBay listings...</p></div>}

              {!ebayLoading && ebayData && ebayData.avg && (
                <>
                  {/* Price Summary */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
                    <div style={{ padding: 10, background: "var(--bg-elevated)", borderRadius: "var(--radius-sm)", textAlign: "center", minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4 }}>Average</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "var(--green)", fontFamily: "JetBrains Mono", overflow: "hidden", textOverflow: "ellipsis" }}>{fmtC(ebayData.avg)}</div>
                    </div>
                    <div style={{ padding: 10, background: "var(--bg-elevated)", borderRadius: "var(--radius-sm)", textAlign: "center", minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4 }}>Low</div>
                      <div style={{ fontSize: 15, fontWeight: 800, fontFamily: "JetBrains Mono", overflow: "hidden", textOverflow: "ellipsis" }}>{fmtC(ebayData.low)}</div>
                    </div>
                    <div style={{ padding: 10, background: "var(--bg-elevated)", borderRadius: "var(--radius-sm)", textAlign: "center", minWidth: 0 }}>
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 4 }}>High</div>
                      <div style={{ fontSize: 15, fontWeight: 800, fontFamily: "JetBrains Mono", overflow: "hidden", textOverflow: "ellipsis" }}>{fmtC(ebayData.high)}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>{ebayData.count} active listings found on eBay</div>

                  {/* Active Listings */}
                  {ebayData.listings && ebayData.listings.length > 0 && (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Active Listings</div>
                      {ebayData.listings.map((item) => (
                        <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none", color: "inherit", display: "block", overflow: "hidden" }}>
                          <div className="card" style={{ marginBottom: 8, padding: 10, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", overflow: "hidden", maxWidth: "100%" }}>
                            {item.image && <img src={item.image} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />}
                            <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                              <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</div>
                              <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {item.condition}{item.seller && ` · ${item.seller}`}
                              </div>
                            </div>
                            <div style={{ textAlign: "right", flexShrink: 0, minWidth: 60 }}>
                              <div style={{ fontSize: 13, fontWeight: 800, fontFamily: "JetBrains Mono", color: "var(--accent)", whiteSpace: "nowrap" }}>{fmtC(item.price)}</div>
                              {item.shippingCost != null && <div style={{ fontSize: 9, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{item.shippingCost === 0 ? "Free" : `+${fmtC(item.shippingCost)}`}</div>}
                            </div>
                          </div>
                        </a>
                      ))}
                    </>
                  )}
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 12 }}>Prices from eBay active listings (Buy It Now).</div>
                </>
              )}

              {!ebayLoading && ebayData && !ebayData.avg && (
                <div className="price-coming-soon">
                  <div style={{ fontSize: 36, marginBottom: 12 }}>📦</div>
                  <h4>{ebayData.error ? "eBay Error" : "No eBay Listings Found"}</h4>
                  <p>{ebayData.error || "No active listings were found on eBay for this card. Try checking back later."}</p>
                  <button className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={() => { setEbayData(null); }}>Try Again</button>
                </div>
              )}

              {!ebayLoading && !ebayData && (
                <div className="price-coming-soon">
                  <div style={{ fontSize: 36, marginBottom: 12 }}>📦</div>
                  <h4>Click to Load eBay Data</h4>
                  <p>eBay listings will be fetched when you select this tab.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Price Chart Tab ── */}
          {priceTab === "chart" && (
            <div>
              {historyLoading && <div style={{ textAlign: "center", padding: 40, color: "var(--text-secondary)" }}><Icons.Spinner /><p style={{ marginTop: 12 }}>Loading price history...</p></div>}

              {!historyLoading && priceHistory.length > 1 && (
                <>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Price History — Last {priceHistory.length} Days</div>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={priceHistory}>
                      <defs><linearGradient id="colorPriceDetail" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B8BF5" stopOpacity={0.25} /><stop offset="95%" stopColor="#3B8BF5" stopOpacity={0} /></linearGradient></defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1C3D6B" />
                      <XAxis dataKey="date" tick={{ fill: "#4D6A8F", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fill: "#4D6A8F", fontSize: 11 }} tickFormatter={(v) => "$" + v.toFixed(0)} width={60} domain={['auto', 'auto']} />
                      <Tooltip contentStyle={{ background: "#163660", border: "1px solid #1C3D6B", borderRadius: 8, fontSize: 13, fontFamily: "JetBrains Mono" }} formatter={(v) => [fmtC(v), "Market Price"]} labelFormatter={(l) => fmtDate(l)} />
                      <Area type="monotone" dataKey="price" stroke="#3B8BF5" strokeWidth={2} fill="url(#colorPriceDetail)" />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>Real price data from TCGPlayer via OPTCG API. Updated daily.</div>
                </>
              )}

              {!historyLoading && priceHistory.length <= 1 && (
                <div className="price-coming-soon">
                  <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
                  <h4>Price History Not Available</h4>
                  <p>
                    {card.game === "one_piece"
                      ? "Historical price data could not be loaded for this card. The OPTCG API provides up to 14 days of real price history when available."
                      : "The Pokémon TCG API does not provide historical price data. We are working on integrating additional sources (eBay sold listings) to provide real price history in future updates."}
                  </p>
                  {latestPrice > 0 && (
                    <div style={{ marginTop: 16, padding: 14, background: "var(--bg-elevated)", borderRadius: "var(--radius-sm)", fontSize: 13 }}>
                      <strong style={{ color: "var(--text-primary)" }}>Current price:</strong>{" "}
                      <span style={{ color: "var(--green)", fontFamily: "JetBrains Mono", fontWeight: 700 }}>{fmtC(latestPrice)}</span>
                      <span style={{ color: "var(--text-muted)", marginLeft: 8 }}>from {getPriceSource(card)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Your Entries ── */}
      {userItems.length > 0 && (
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Your Entries</h3>
          {userItems.map((item) => (
            <div key={item.id} className="card" style={{ marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div><div style={{ fontSize: 14, fontWeight: 600 }}>{item.quantity}× @ {fmtC(item.purchase_price)} each</div><div style={{ fontSize: 13, color: "var(--text-secondary)" }}>{fmtDate(item.purchase_date)} · {item.condition} · {item.language}</div>{item.notes && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{item.notes}</div>}</div>
              <div style={{ textAlign: "right" }}><div className="mono" style={{ fontSize: 14 }}>Cost: {fmtC(item.purchase_price * item.quantity)}</div><div className={`mono ${(latestPrice - item.purchase_price) >= 0 ? "gain-positive" : "gain-negative"}`} style={{ fontSize: 14 }}>P&L: {(latestPrice - item.purchase_price) >= 0 ? "+" : ""}{fmtC((latestPrice - item.purchase_price) * item.quantity)}</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// LANDING PAGE
// ═══════════════════════════════════════════════════════════════════
export const LandingPage = ({ onNavigate }) => (
  <div className="landing">
    <div style={{ padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div className="nav-brand"><img src="/logos/honki-logo.png" alt="Honki TCG Tracker" style={{ height: 40 }} /></div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-ghost" onClick={() => onNavigate("login")}>Log In</button>
        <button className="btn btn-highlight" onClick={() => onNavigate("signup")}>Sign Up</button>
      </div>
    </div>
    <div className="hero">
      <div style={{ marginBottom: 24 }}><img src="/logos/honki-logo.png" alt="Honki TCG Tracker" style={{ height: 100 }} /></div>
      <div className="hero-badge" style={{ color: "#FFFFFF", background: "rgba(255,255,255,0.1)", borderColor: "rgba(255,255,255,0.2)" }}><Icons.Star /> Portfolio Tracker for TCG Collectors</div>
      <h1>Track your <span className="gradient">card collection</span> like a pro</h1>
      <p>Monitor your Pokémon and One Piece card investments. Know what your collection is worth — in real time.</p>
      <div className="hero-actions">
        <button className="btn btn-highlight btn-lg" onClick={() => onNavigate("signup")}>Get Started Free</button>
        <button className="btn btn-secondary btn-lg" onClick={() => onNavigate("login")}>Log In</button>
      </div>
    </div>
    <div className="features">
      <div className="features-grid">
        {[
          { icon: <Icons.Collection />, title: "Collection Management", desc: "Search thousands of real Pokémon and One Piece cards. Track quantity, purchase price, condition, and language." },
          { icon: <Icons.TrendUp />, title: "Live Valuation", desc: "See estimated market values powered by TCGPlayer and eBay data. Track unrealized gains and losses." },
          { icon: <Icons.Home />, title: "Portfolio Dashboard", desc: "Get a birds-eye view of your investments. Total value, top gainers, losers, and performance charts." },
        ].map((f, i) => (
          <div className="feature-card" key={i}><div className="feature-icon">{f.icon}</div><div className="feature-title">{f.title}</div><div className="feature-desc">{f.desc}</div></div>
        ))}
      </div>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════
// AUTH PAGE
// ═══════════════════════════════════════════════════════════════════
export const AuthPage = ({ mode, onToggle }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError("");
    if (mode === "signup" && !name.trim()) return setError("Name is required");
    if (!email.trim()) return setError("Email is required");
    if (!password.trim() || password.length < 6) return setError("Password must be at least 6 characters");
    setLoading(true);
    try {
      if (mode === "signup") {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: name });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      const msgs = { "auth/email-already-in-use": "This email is already registered", "auth/invalid-email": "Invalid email address", "auth/weak-password": "Password must be at least 6 characters", "auth/user-not-found": "No account found with this email", "auth/wrong-password": "Incorrect password", "auth/invalid-credential": "Invalid email or password", "auth/too-many-requests": "Too many attempts. Please try again later" };
      setError(msgs[err.code] || err.message);
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError(""); setLoading(true);
    try { await signInWithPopup(auth, googleProvider); } catch (err) { if (err.code !== "auth/popup-closed-by-user") setError(err.message); setLoading(false); }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="nav-brand" style={{ justifyContent: "center", marginBottom: 28 }}><img src="/logos/honki-logo.png" alt="Honki TCG Tracker" style={{ height: 64 }} /></div>
        <div className="auth-title">{mode === "signup" ? "Create Account" : "Welcome Back"}</div>
        <div className="auth-subtitle">{mode === "signup" ? "Start tracking your card collection" : "Log in to your portfolio"}</div>
        {error && <div className="auth-error">{error}</div>}
        <button className="btn btn-google btn-full" onClick={handleGoogle} disabled={loading} style={{ marginBottom: 8 }}><Icons.Google /> Continue with Google</button>
        <div className="auth-divider">or</div>
        {mode === "signup" && <div className="input-group"><label className="input-label">Name</label><input className="input" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} /></div>}
        <div className="input-group"><label className="input-label">Email</label><input className="input" type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="input-group"><label className="input-label">Password</label><input className="input" type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSubmit()} /></div>
        <button className="btn btn-highlight btn-full btn-lg" onClick={handleSubmit} disabled={loading} style={{ marginTop: 8 }}>
          {loading ? <><Icons.Spinner /> {mode === "signup" ? "Creating..." : "Logging in..."}</> : (mode === "signup" ? "Create Account" : "Log In")}
        </button>
        <div className="auth-toggle">
          {mode === "signup" ? <>Already have an account? <a onClick={onToggle}>Log In</a></> : <>Don't have an account? <a onClick={onToggle}>Sign Up</a></>}
        </div>
      </div>
    </div>
  );
};
